import { useEffect, useMemo, useState } from 'react';
import ArrowLeftLineIcon from 'remixicon-react/ArrowLeftLineIcon';
import { useForm, Controller } from "react-hook-form";
import ReactSelect from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import Papa from 'papaparse'
import CheckLineIcon from 'remixicon-react/CheckLineIcon';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import DownloadCloud2sLineIcon from 'remixicon-react/DownloadCloud2LineIcon';
import CardLayout from '../../../components/utils/CardLayout'
import Button from '../../../components/utils/Button'
import Table from '../../../components/utils/Table';
import { isKeyUnique, validateIMEI, validateSNo, validatePin, selectCharacters, twoDigitDecimal } from '../../../helpers/commonFunction';
import activationRequestActions from '../../../redux/activationRequest/actions';
import './../ActivationRequest.css';
import Alert from '../../../components/utils/Alert';
import planAction from '../../../redux/plan/actions';
import shortCodeAction from '../../../redux/shortCodeData/actions';
import additionalServiceActions from '../../../redux/additionalService/actions';

const { addBulkActivation, resetValidateMessage, getDealerPostFixEmail, getPrepaidIqAddresses, validateSerialNumber, checkImeiNumber } = activationRequestActions;
const BASE_URL = process.env.REACT_APP_SITE_BASE_URL;

const { createBulkAdditionalService, resetMessage } = additionalServiceActions;
const { getPlan } = planAction;
const { getSimType } = shortCodeAction;

const ACTIVATION_LIMIT = 300;
const allowedActions = [
    'Top Up',
    'Change MDN',
    'Reconnect',
    'Disconnect',
    'Extend',
    'Suspend'
];

const BulkAdditionalServiceForm = ({ back, searchData }) => {
    const dispatch = useDispatch();
    const { networkResult } = useSelector((state) => state.network);
    const { planResult } = useSelector((state) => state.plan);
    const { tokenData } = useSelector((state) => state.auth);
    const { shortCodeSimType } = useSelector((state) => state.shortCode);
    const { planAddonResult, addBulkActivationMessage, addBulkActivationError, addBulkActivationLoading, getPrepaidIqAddressesData, validateSerialNumberData, validateSerialNumberLoading, checkImeiNumberData } = useSelector((state) => state.activationRequest);
    const { addonResult } = useSelector((state) => state.addonService);
    const {
        createBulkAdditionalServiceLoading,
        createBulkAdditionalServiceMessage,
        createBulkAdditionalServiceError
    } = useSelector((state) => state.additionalService);
    const [previewData, setPreviewData] = useState([]);
    const [showPreviewType, setShowPreviewType] = useState(2);
    const [showAlert, setShowAlert] = useState(false);
    const [alertType, setAlertType] = useState('');
    const [alertText, setAlertText] = useState('');
    const [accountBalance, setAccountBalance] = useState(0);

    useEffect(() => {
        tokenData && tokenData.length > 0 && tokenData.map((data) => {
            setAccountBalance(data.AccountBalance);
        });
    }, [tokenData]);

    useEffect(() => {
        if (createBulkAdditionalServiceError !== null && createBulkAdditionalServiceError !== undefined) {
            dispatch(resetMessage());
            displayAlert(createBulkAdditionalServiceError, 'alert-danger');
        }
    }, [createBulkAdditionalServiceError]);

    useEffect(() => {
        if (createBulkAdditionalServiceMessage) {
            back();
            dispatch(resetMessage());
        }
    }, [createBulkAdditionalServiceMessage]);

    const { register, handleSubmit, formState: { errors }, watch, reset, setValue, setError, clearErrors, control, } = useForm({});

    // NEW CODE STARTS FROM HERE
    const displayAlert = (text, type) => {
        setAlertText(text);
        setAlertType(type);
        setShowAlert(true);
    };

    const alertCancel = () => {
        setShowAlert(false);
        setAlertText('');
        setAlertType('');
    }

    const previewColumns = [
        { displayName: 'ICCID', key: 'iccid' },
        { displayName: 'MDN', key: 'mdn' },
        { displayName: 'ACTION', key: 'action' },
        { displayName: 'EXTRA', key: 'extra' },
        { displayName: 'REASON', key: 'reason' },
        { displayName: 'VALID', key: 'valid' },
    ];

    const downloadSampleFile = () => {
        let url = `${BASE_URL}/assets/csv/additional-service-sample-file.csv`;
        let link = document.createElement('a');
        link.href = url;
        link.download = 'additional-service-sample-file.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const handleFileChange = (e) => {
        clearErrors(['uploadedFile']);
        setPreviewData([]);
        const fileReader = new FileReader();
        let file = e.target.files[0];

        if (!file) {
            setError("uploadedFile", { type: "custom", message: "Csv is required!" })
            return;
        } else {
            let fileNameArr = file.name.split('.');
            let fileExt = fileNameArr[fileNameArr.length - 1];

            if (!["csv"].includes(fileExt.toLowerCase())) {
                setError("uploadedFile", { type: "custom", message: "Invalid file type. Only CSV file allowed." })
                return;
            }
        }

        if (file) {
            fileReader.onload = async ({ target }) => {
                const csvOutput = Papa.parse(target.result, { header: true, skipEmptyLines: true });
                const parsedData = csvOutput?.data;
                const columns = Object.keys(parsedData[0]);

                if (parsedData.length > ACTIVATION_LIMIT) {
                    setError("uploadedFile", { type: "custom", message: `Maximum ${ACTIVATION_LIMIT} rows are allowed` })
                    return;
                }

                let specifiedColumn = ['ICCID', 'MDN', 'Action', 'Extra'];

                let isValidCsvHeading = specifiedColumn.every((item) => columns.includes(item));

                if (isValidCsvHeading && columns.length === specifiedColumn.length) {
                    const invalidRows = parsedData.filter(item => !allowedActions.includes(item.Action));
                    if (invalidRows.length === 0) {
                        let dataSource = parsedData.map((item) => {
                            let error = false;
                            let reason = "";

                            if (item.ICCID === item.MDN && item.MDN === item.Extra) {
                                error = true;
                                reason = "Same value found in ICCID, MDN and Extra field";
                            }

                            if (item.Action === 'Top Up' && (!item.ICCID && !item.MDN || !item.Extra)) {
                                error = true;
                                reason = "For Top Up action, either ICCID or MDN is required and Extra is required";
                            }

                            if (item.Action === 'Change MDN' && item.ICCID === "") {
                                error = true;
                                reason = "ICCID is required for Change MDN action";
                            }

                            if (item.Action === 'Reconnect' && item.ICCID === "" && item.MDN === "") {
                                error = true;
                                reason = "Either ICCID or MDN is required for Reconnect action";
                            }

                            if (item.Action === 'Disconnect' && item.ICCID === "" && item.MDN === "") {
                                error = true;
                                reason = "Either ICCID or MDN is required for Disconnect action";
                            }

                            if (item.Action === 'Extend' && item.ICCID === "" && item.MDN === "") {
                                error = true;
                                reason = "Either ICCID or MDN is required for Extend action";
                            }

                            if (item.Action === 'Suspend' && item.ICCID === "" && item.MDN === "") {
                                error = true;
                                reason = "Either ICCID or MDN is required for Suspend action";
                            }

                            return {
                                iccid: item.ICCID,
                                mdn: item.MDN,
                                action: item.Action,
                                extra: item.Extra,
                                reason: reason,
                                valid: error ? <CloseLineIcon color='#e74c3c' /> : <CheckLineIcon color='#2ecc71' />,
                                status: error ? 0 : 1
                            }
                        });

                        setPreviewData(dataSource);
                    } else {
                        setError("uploadedFile", { type: "custom", message: `Invalid actions found in CSV` });
                    }
                } else {
                    setError("uploadedFile", { type: "custom", message: "Invalid Csv Selected" })
                }
            };
            fileReader.readAsText(file);
        }
    }

    const onSubmit = (data) => {
        const req = previewData.map((item) => {
            return {
                iccid: item.iccid,
                mdn: item.mdn,
                action: item.action,
                extra: item.extra
            }
        });

        const requestData = {
            requestData: req
        }

        dispatch(createBulkAdditionalService(requestData));

    }

    const previewArr = useMemo(() => {
        if (showPreviewType == 2) {
            return previewData;
        } else {
            return previewData.filter(item => item.status == showPreviewType);
        }
    }, [previewData, showPreviewType]);

    return (
        <CardLayout title="Add Additional Service">
            <div className="row mb-3">
                <div className='col-lg-12 text-right'>
                    <button type="button" onClick={() => downloadSampleFile()} style={{ background: '#2ecc71', color: '#fff', pointerEvents: 'auto' }} className={`btn font-weight-bold mr-2`}><DownloadCloud2sLineIcon size={16} />Sample File</button>
                    <button type="button" onClick={() => back()} className={`btn btn-danger font-weight-bold`}><ArrowLeftLineIcon size={16} />Back</button>
                </div>
            </div>

            <div className='container'>
                <form onSubmit={handleSubmit(onSubmit)}>

                    {/* FORM FIELDS STARTS FROM HERE */}
                    <div className="form-row">

                        <div className="col-md-6 mb-3 p-0">
                            <div className="input-group">
                                <label htmlFor="csvFile"><span className='text-danger'>*</span>Upload File </label>
                                <input
                                    type="file"
                                    className="form-control-file"
                                    name='uploadedFile'
                                    id="csvFile"
                                    accept='.csv'
                                    {...register('uploadedFile', { required: 'CSV is required' })}
                                    onChange={(e) => handleFileChange(e)}
                                />
                                <small className='text-danger'>{`Note : Only ${ACTIVATION_LIMIT} Rows are allowed`}</small>
                                {errors && errors.uploadedFile && (<div className="invalid-feedback">{errors.uploadedFile.message}</div>)}

                            </div>
                        </div>

                    </div>
                    {/* FORM FIELDS END HERE */}

                    {
                        previewData.length > 0 &&
                        <>
                            <hr />
                            <div className='row p-0 m-0' style={{ cursor: 'pointer' }}>
                                <div className="col-md-4 d-flex justify-content-around align-items-center py-3 preview-box" onClick={() => { setShowPreviewType(2) }}>
                                    <h4>Total Submitted : {previewData.length}</h4>
                                </div>
                                <div className="col-md-4 d-flex justify-content-around align-items-center preview-box" onClick={() => { setShowPreviewType(1) }}>
                                    <h4 className='text-success'>Valid : {previewData.filter((item) => item.status === 1).length}</h4>
                                </div>
                                <div className="col-md-4 d-flex justify-content-around align-items-center preview-box" onClick={() => { setShowPreviewType(0) }}>
                                    <h4 className='text-danger'>Invalid : {previewData.filter((item) => item.status === 0).length}</h4>
                                </div>
                            </div>
                            <div className="col-md-12 mb-3 p-0">
                                <Table
                                    columns={previewColumns}
                                    loading={validateSerialNumberLoading}
                                    data={previewArr}
                                    pagination={false}
                                />
                            </div>
                        </>
                    }
                    <hr />

                    {showAlert && <Alert type={alertType} text={alertText} closeBtn={alertCancel} />}

                    <div className='text-right row'>
                        <div className="col-md-12">
                            <button type="button" className="btn btn-secondary mr-2" onClick={() => back()}>Close</button>
                            <Button
                                title="Submit"
                                disable={previewData.filter((item) => item.status === 0).length > 0 || previewData.length === 0}
                                style="minWidth:80px"
                                loading={createBulkAdditionalServiceLoading}
                                buttonClass="btn btn-primary"
                                buttonType='submit'
                            />
                        </div>
                    </div>

                </form>
            </div>

        </CardLayout>
    )
}

export default BulkAdditionalServiceForm;